import { ReactElement, useState, useEffect, MutableRefObject } from 'react';
import { numberToLetters } from '../../../../utils/letterNumber';
import EquipmentCatalog from './EquipmentCatalog';
import { useFormContext } from 'react-hook-form';
import { DISC_LETTER_NUMBER } from '../../../../constants/apiConstants';
import { useEquipmentCatalog } from '../../../../hooks/useEquipment';
import { useEquipmentState } from '../../../../hooks/useEquipmentState';
import { EQUIPMENT_ITEMS, EQUIPMENT_ITEMS_BY_DISPLAY } from 'utils/equipmentNames';
import Tooltip from '../../../../components/Tooltip';

interface CircumferenceReadiness {
  complete: boolean;
  missing: string[];
}

function diagnoseCircumferenceReadiness(
  config: any,
): CircumferenceReadiness {
  const missing: string[] = [];

  if (config.speed_sensor_pos == null) {
    missing.push('Speed Sensor Position');
    missing.push('Tyre');
    missing.push('Wheel');
    missing.push('Tyre Pressure');
    return { complete: false, missing };
  }

  if (config.speed_sensor_pos === 1) {
    // Front
    if (!config.tyre_front_id) missing.push('Tyre (Front)');
    if (!config.wheel_front_id) missing.push('Wheel (Front)');
    if (!config.tyre_front_pressure) missing.push('Tyre Pressure (Front)');
  } else {
    // Rear (0)
    if (!config.tyre_rear_id) missing.push('Tyre (Rear)');
    if (!config.wheel_rear_id) missing.push('Wheel (Rear)');
    if (!config.tyre_rear_pressure) missing.push('Tyre Pressure (Rear)');
  }

  return { complete: missing.length === 0, missing };
}

interface SessionEquipmentListProps {
  session: any;
  configControls: any;
  readOnly?: boolean;
  onSaveNeeded?: () => Promise<void>;
  markUnsavedChange?: (key: string) => void;
  clearUnsavedChange?: (key: string) => void;
  equipmentSaveRef?: MutableRefObject<(() => Promise<void>) | null>;
}

const SessionEquipmentList = ({
  session,
  configControls,
  readOnly = false,
  onSaveNeeded,
  markUnsavedChange,
  clearUnsavedChange,
  equipmentSaveRef,
}: SessionEquipmentListProps): ReactElement => {
  // Get form context for editing
  const formMethods = readOnly ? null : useFormContext();

  // Fetch the full equipment catalog
  const { data: equipmentCatalog, isLoading: isLoadingCatalog } = useEquipmentCatalog();

  // Use equipment state hook
  const { equipmentParameters, setEquipmentParameters, equipmentSelected, setEquipmentSelected, saveEquipmentChanges } =
    useEquipmentState({
      session,
      isEdit: !readOnly,
      onSaveNeeded,
      markUnsavedChange,
      clearUnsavedChange,
    });

  // State to track selected parameters from EquipmentCatalog
  const [selectedParameters, setSelectedParameters] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<any>({});

  // Helper function to convert equipment UUIDs to full objects
  const convertUUIDsToEquipment = (equipmentUUIDs: any, catalog: any) => {
    if (!catalog || !equipmentUUIDs) return {};

    const result: any = {
      frames: [],
      tyres: [],
      wheels_front: [],
      wheels_rear: [],
    };

    // Convert each equipment type
    if (equipmentUUIDs.frames && catalog.frames) {
      result.frames = equipmentUUIDs.frames
        .map((id: string) => catalog.frames.find((item: any) => item.id === id))
        .filter(Boolean)
        .map((item: any) => ({
          table: 'frames',
          id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
        }));
    }

    if (equipmentUUIDs.tyres && catalog.tyres) {
      result.tyres = equipmentUUIDs.tyres
        .map((id: string) => catalog.tyres.find((item: any) => item.id === id))
        .filter(Boolean)
        .map((item: any) => ({
          table: 'tyres',
          id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
        }));
    }

    if (equipmentUUIDs.wheels_front && catalog.wheels_front) {
      result.wheels_front = equipmentUUIDs.wheels_front
        .map((id: string) => catalog.wheels_front.find((item: any) => item.id === id))
        .filter(Boolean)
        .map((item: any) => ({
          table: 'wheels_front',
          id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
        }));
    }

    if (equipmentUUIDs.wheels_rear && catalog.wheels_rear) {
      result.wheels_rear = equipmentUUIDs.wheels_rear
        .map((id: string) => catalog.wheels_rear.find((item: any) => item.id === id))
        .filter(Boolean)
        .map((item: any) => ({
          table: 'wheels_rear',
          id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
        }));
    }

    return result;
  };

  // Helper function to convert database parameter names to display names using EquipmentNames
  const convertDbParametersToDisplay = (dbParameters: string[]): string[] => {
    return dbParameters.map((param) => {
      // Try to find the equipment property by name
      const equipmentProperty = EQUIPMENT_ITEMS[param];
      if (equipmentProperty) {
        return equipmentProperty.displayValue;
      }

      // Fallback: return the parameter as-is if not found
      return param;
    });
  };

  // Convert equipment parameters to display format for table headers
  useEffect(() => {
    const displayParameters = convertDbParametersToDisplay(equipmentParameters);
    setSelectedParameters(displayParameters);
  }, [equipmentParameters]);

  // Convert equipment UUIDs to full objects for display
  useEffect(() => {
    if (equipmentCatalog && Object.keys(equipmentSelected).length > 0) {
      const fullEquipment = convertUUIDsToEquipment(equipmentSelected, equipmentCatalog);
      setSelectedEquipment(fullEquipment);
    } else {
      setSelectedEquipment(equipmentSelected);
    }
  }, [equipmentSelected, equipmentCatalog]);

  // Assign save function to ref so parent can call it
  useEffect(() => {
    if (equipmentSaveRef) {
      equipmentSaveRef.current = saveEquipmentChanges;
    }
  }, [saveEquipmentChanges, equipmentSaveRef]);

  // Helper function to format column headers: title case with underscores replaced by spaces
  const formatColumnHeader = (header: string): string => {
    return header
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get configs that are not deleted and don't have letter_number === 2147483647
  const validConfigs = configControls.filter(
    (config: any) => !config.isDeleted && config.letter_number !== DISC_LETTER_NUMBER,
  );

  // Helper function to get equipment name by ID and type
  const getEquipmentName = (equipmentId: string, equipmentType: string) => {
    // First check selectedEquipment
    if (selectedEquipment && selectedEquipment[equipmentType]) {
      const equipment = selectedEquipment[equipmentType].find((item: any) => item.id === equipmentId);
      if (equipment) return equipment.name;
    }

    // Fallback to equipmentCatalog if not found in selectedEquipment
    if (equipmentCatalog && equipmentCatalog[equipmentType]) {
      const equipment = equipmentCatalog[equipmentType].find((item: any) => item.id === equipmentId);
      if (equipment) return equipment.name;
    }

    return null;
  };

  // Helper function to convert DB column names to display values using EquipmentNames
  const getEquipmentDisplayValue = (param: string, config: any) => {
    const paramLower = param.toLowerCase();

    // Try to find equipment property by param name or display value
    const equipmentProperty = EQUIPMENT_ITEMS[paramLower] || EQUIPMENT_ITEMS_BY_DISPLAY[param];

    if (equipmentProperty) {
      // Get the first property that this equipment writes to
      const propWrite = equipmentProperty.propWrite();
      const firstProp = propWrite[0];

      if (equipmentProperty.catalogTable) {
        // This is an equipment item (frame, tyre, wheel)
        const equipmentId = config?.[firstProp.propName];
        if (!equipmentId) return '-';

        const name = getEquipmentName(equipmentId, equipmentProperty.catalogTable);
        return name || equipmentId;
      } else if (equipmentProperty.type === 'number') {
        // This is a numeric input (pressure)
        const value = config?.[firstProp.propName];
        return value ? parseFloat(value).toFixed(1) : '-';
      } else if (equipmentProperty.name === 'damper') {
        // This is damper settings
        return config?.damper_settings?.display_value || '-';
      }
    }

    // Fallback: return the value as-is
    return config?.[param] || config?.[paramLower] || '-';
  };

  // Helper function to render cell content based on parameter type
  const renderCellContent = (param: string, originalConfig: any, configIndex: number) => {
    if (readOnly) {
      // Read-only display logic - use the new helper function
      return getEquipmentDisplayValue(param, originalConfig);
    } else {
      // Edit mode - render form inputs
      // Try to find mapping by param name or by display value
      const itemMapping = EQUIPMENT_ITEMS[param] || EQUIPMENT_ITEMS_BY_DISPLAY[param];
      const propName = itemMapping?.propWrite()[0].propName || param;
      if (itemMapping && itemMapping.catalogTable) {
        return (
          <select
            {...formMethods?.register(`configs.${configIndex}.${propName}`)}
            onChange={(e) => {
              // Update both front and rear tyres and mark as dirty
              itemMapping.propWrite(e.target.value).forEach((prop: any) => {
                formMethods?.setValue(`configs.${configIndex}.${prop.propName}`, prop.output, { shouldDirty: true });
              });
            }}
            className="cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-1 cr-text-sm"
          >
            <option value="">Select {itemMapping.displayValue}</option>
            {selectedEquipment[itemMapping.catalogTable]?.map((obj: any) => (
              <option key={obj.id} value={obj.id}>
                {obj.name}
              </option>
            ))}
          </select>
        );
      } else if (itemMapping?.type) {
        return (
          <input
            type={itemMapping.type}
            step={itemMapping.step}
            {...formMethods?.register(`configs.${configIndex}.${propName}`)}
            defaultValue={originalConfig ? [propName] : ''}
            onChange={(e) => {
              // Update both front and rear tyres and mark as dirty
              itemMapping.propWrite(e.target.value).forEach((prop: any) => {
                formMethods?.setValue(`configs.${configIndex}.${prop.propName}`, prop.output, { shouldDirty: true });
              });
            }}
            className="cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-1 cr-text-sm"
            // placeholder="0.0"
          />
        );
      } else {
        return (
          <input
            type="text"
            {...formMethods?.register(`configs.${configIndex}.${param}`)}
            onChange={(e) => {
              formMethods?.setValue(`configs.${configIndex}.${param}`, e.target.value, { shouldDirty: true });
            }}
            className="cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-1 cr-text-sm"
          />
        );
      }
    }
  };

  return (
    <div className="cr-flex cr-flex-col cr-gap-6">
      {/* Equipment Catalog Component - only show in edit mode */}
      {!readOnly && (
        <EquipmentCatalog
          session={session}
          equipmentParameters={equipmentParameters}
          setEquipmentParameters={setEquipmentParameters}
          equipmentSelected={equipmentSelected}
          setEquipmentSelected={setEquipmentSelected}
        />
      )}

      {isLoadingCatalog && (
        <div className="cr-py-4 cr-text-center">
          <span className="cr-text-gray-500">Loading equipment catalog...</span>
        </div>
      )}

      <div className="cr-flex cr-flex-col">
        <div
          className={'cr-grid cr-text-center md:cr-m-2'}
          style={{
            gridTemplateColumns: `1fr 0.8fr 1.2fr ${selectedParameters.map(() => '1fr').join(' ')} 2.5rem`,
          }}
        >
          <div
            className={
              'cr-mb-4 cr-grid cr-grid-cols-subgrid cr-text-2xs cr-font-bold cr-uppercase cr-text-cirrusBlue-400 cr-font-main cr-font-medium md:cr-text-sm'
            }
            style={{
              gridColumn: `span ${4 + selectedParameters.length}`,
            }}
          >
            <div className="cr-truncate cr-text-center">Config</div>
            <div className="cr-truncate cr-text-center">Parent</div>
            <div className="cr-truncate cr-text-center">Description</div>
            {selectedParameters.map((param) => (
              <div key={param} className="cr-truncate cr-text-center">
                {formatColumnHeader(param)}
              </div>
            ))}
            <div />
          </div>
          {validConfigs.map((config: any, index: number) => {
            // Find the original config from session to get complete data
            const originalConfig = session.configs?.find((c: any) => c?.id === config?.id);
            const parentConfig = originalConfig?.baseline_id
              ? session.configs?.find((c: any) => c?.id === originalConfig.baseline_id)
              : null;

            const readiness = diagnoseCircumferenceReadiness(
              originalConfig || config,
            );

            return (
              <div
                key={config.fieldId || config.id}
                className={
                  'cr-mb-2 cr-grid cr-grid-cols-subgrid cr-rounded-lg cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-50'
                }
                style={{ gridColumn: `span ${4 + selectedParameters.length}` }}
              >
                <div className="cr-flex cr-items-center cr-justify-center cr-py-3 cr-text-lg cr-font-bold">
                  {config.letter_number !== undefined ? numberToLetters(config.letter_number) : '-'}
                </div>
                <div className="cr-flex cr-items-center cr-justify-center cr-py-3 cr-text-lg">
                  {parentConfig?.letter_number !== undefined ? numberToLetters(parentConfig.letter_number) : '-'}
                </div>
                <div className="cr-flex cr-items-center cr-justify-center cr-py-3 cr-text-lg">
                  {readOnly ? (
                    config.description || '-'
                  ) : (
                    <input
                      type="text"
                      {...formMethods?.register(`configs.${index}.description`)}
                      defaultValue={config.description || ''}
                      onChange={(e) => {
                        formMethods?.setValue(`configs.${index}.description`, e.target.value, { shouldDirty: true });
                      }}
                      className="cr-mx-2 cr-w-full cr-rounded cr-border cr-border-gray-300 cr-p-1 cr-text-sm"
                      placeholder="Enter description"
                    />
                  )}
                </div>
                {selectedParameters.map((param) => (
                  <div key={param} className="cr-flex cr-items-center cr-justify-center cr-py-3 cr-text-lg">
                    {isLoadingCatalog ? '...' : renderCellContent(param, originalConfig, index)}
                  </div>
                ))}
                <div className="cr-flex cr-items-center cr-justify-center cr-py-3">
                  {readiness.complete ? (
                    <svg
                      className="cr-h-5 cr-w-5 cr-text-green-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  ) : (
                    <Tooltip
                      title={
                        <div className="cr-flex cr-flex-col cr-gap-0.5">
                          <span className="cr-font-semibold">
                            Missing for circumference:
                          </span>
                          {readiness.missing.map((f) => (
                            <span key={f}>• {f}</span>
                          ))}
                        </div>
                      }
                      placement="left"
                    >
                      <svg
                        className="cr-h-5 cr-w-5 cr-cursor-help cr-text-orange-500"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        viewBox="0 0 24 24"
                      >
                        <path d="M 17.2 5.3 A 10 10 0 1 0 18.7 8.5" />
                      </svg>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {validConfigs.length === 0 && (
          <div className="cr-py-8 cr-text-center cr-text-gray-500">No configurations available</div>
        )}
        {selectedParameters.length === 0 && validConfigs.length > 0 && (
          <div className="cr-mt-4 cr-text-center cr-text-sm cr-text-gray-500">
            No parameters selected in Equipment section above
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionEquipmentList;
